/**
 * Sandbox — OutpostForge frontend
 * What-if mode: hypothetical racks/droplets (the v1 model) validated and
 * visualized with slotting-core, persisted in localStorage. Scenarios can be
 * saved, reloaded, and compared (totals + aggregate instance diff).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  computeDropletLayout,
  diffInstanceMaps,
  INSTANCE_CATALOG,
  validateDroplet,
  type InstanceMap,
} from 'slotting-core';
import { Button } from '../components/atoms/Button';
import { Chip } from '../components/atoms/Chip';
import { Input } from '../components/atoms/Input';
import { Select } from '../components/atoms/Select';
import { DiffTable } from '../components/molecules/DiffTable';
import { ResourceBars } from '../components/molecules/ResourceBars';
import { StepperRow } from '../components/molecules/StepperRow';
import { CapacityVisual } from '../components/organisms/CapacityVisual';
import { outpostRepository } from '../repositories/OutpostRepository';
import {
  aggregateInstances,
  cloneAssetToSandbox,
  cloneRackToSandbox,
  computeFamilyKeys,
  dropletToInstancePoolsJson,
  EMPTY_SANDBOX,
  exportFileName,
  loadSandbox,
  newId,
  saveSandbox,
  scenarioTotals,
  type SandboxDroplet,
  type SandboxRack,
  type SandboxState,
  type Scenario,
} from '../services/sandbox';
import { resolveScenarioStore } from '../services/scenarioStore';
import type { AssetSummary, OutpostSummary } from '../types/outposts';

const FIELD_LABEL_CLASSES = 'flex min-w-[220px] flex-col gap-1 text-xs text-gray-500';
const STATE_MESSAGE_CLASSES = 'py-8 text-center text-gray-500';

/** Save a JSON document to the user's machine. */
function downloadJson(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Export one droplet's InstancePools JSON (usable in Capacity management). */
function exportDroplet(droplet: SandboxDroplet, rackName: string): void {
  downloadJson(exportFileName(droplet, rackName), dropletToInstancePoolsJson(droplet));
}

function DropletCard({
  rack,
  droplet,
  onChange,
  onRemove,
}: {
  rack: SandboxRack;
  droplet: SandboxDroplet;
  onChange: (instances: InstanceMap) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const familyData = INSTANCE_CATALOG[rack.generation]?.[droplet.family];
  if (!familyData) return null;

  const dropletModel = { family: droplet.family, instances: droplet.instances };
  const validation = validateDroplet(dropletModel, familyData, rack.generation);
  const layout = computeDropletLayout(dropletModel, familyData, rack.generation);

  return (
    <div
      className={`mb-3 rounded-lg border border-gray-200 bg-white p-5 shadow ${
        validation.valid ? '' : 'border-l-4 border-l-red-500'
      }`}
      data-status={validation.valid ? 'valid' : 'invalid'}
    >
      <div className="mb-3 flex items-center gap-3">
        <span className="mr-auto font-bold text-gray-900">{droplet.family} droplet</span>
        <span
          className={`rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide ${
            validation.valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
          data-testid="droplet-validity"
        >
          {validation.valid ? 'VALID' : 'INVALID'}
        </span>
        <Button
          variant="secondary"
          small
          data-testid="toggle-droplet"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Close' : 'Edit'}
        </Button>
        <Button
          variant="secondary"
          small
          data-testid="export-droplet"
          title="Save this droplet's InstancePools JSON (usable in Capacity management)"
          onClick={() => exportDroplet(droplet, rack.name)}
        >
          Export JSON
        </Button>
        <Button variant="secondary" small data-testid="remove-droplet" onClick={onRemove}>
          Remove
        </Button>
      </div>

      <CapacityVisual layout={layout} />
      <ResourceBars
        memoryUsed={validation.totals.memoryGiB}
        memoryTotal={familyData.dropletCapacity.memoryGiB}
        vcpusUsed={validation.totals.vCPUs}
        vcpusTotal={familyData.dropletCapacity.vCPUs}
        gpusUsed={validation.totals.gpus}
        gpusTotal={familyData.hasGPU ? familyData.dropletCapacity.gpus : 0}
      />
      {validation.message && (
        <p
          className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="droplet-message"
        >
          {validation.message}
        </p>
      )}

      {expanded && (
        <div className="mt-4 divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
          {familyData.sizes.map((size) => (
            <StepperRow
              key={size.name}
              name={size.name}
              specsLabel={`${size.vCPUs} vCPUs · ${size.memoryGiB} GiB`}
              count={droplet.instances[size.name] ?? 0}
              onChange={(next) => onChange({ ...droplet.instances, [size.name]: next })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DeleteScenarioButton({
  name,
  onDelete,
}: {
  name: string;
  onDelete: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);

  if (!confirming) {
    return (
      <Button
        variant="secondary"
        small
        data-testid={`delete-${name}`}
        onClick={() => setConfirming(true)}
      >
        Delete
      </Button>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <Button
        variant="danger"
        small
        data-testid={`confirm-delete-${name}`}
        disabled={pending}
        onClick={async () => {
          setPending(true);
          await onDelete();
          setPending(false);
        }}
      >
        {pending ? 'Deleting…' : 'Confirm delete'}
      </Button>
      <Button variant="secondary" small disabled={pending} onClick={() => setConfirming(false)}>
        Keep
      </Button>
    </span>
  );
}

function TotalsLine({ label, state }: { label: string; state: SandboxState }) {
  const totals = scenarioTotals(state);
  return (
    <p className="mb-1 text-sm text-gray-900" data-testid={`totals-${label}`}>
      <strong>{label}:</strong> {totals.racks} racks · {totals.droplets} droplets ·{' '}
      {totals.vCPUs} vCPUs · {totals.memoryGiB} GiB
    </p>
  );
}

export function Sandbox() {
  const [state, setState] = useState<SandboxState>(() => loadSandbox());
  const [newRackGeneration, setNewRackGeneration] = useState('gen2');
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenarioName, setScenarioName] = useState('');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [compareA, setCompareA] = useState('');
  const [compareB, setCompareB] = useState('');

  // Persist every change (survives reloads and mode switches)
  useEffect(() => saveSandbox(state), [state]);

  useEffect(() => {
    resolveScenarioStore()
      .then((store) => store.list())
      .then(setScenarios)
      .catch(() => {});
  }, []);

  const addRack = () => {
    const rack: SandboxRack = {
      id: newId('rack'),
      name: `sandbox-rack-${state.racks.length + 1}`,
      generation: newRackGeneration,
      droplets: [],
    };
    setState({ ...state, racks: [...state.racks, rack] });
  };

  const updateRack = (rackId: string, update: (rack: SandboxRack) => SandboxRack) => {
    setState({
      ...state,
      racks: state.racks.map((rack) => (rack.id === rackId ? update(rack) : rack)),
    });
  };

  const removeRack = (rackId: string) => {
    setState({ ...state, racks: state.racks.filter((rack) => rack.id !== rackId) });
  };

  const saveScenario = () => {
    const name = scenarioName.trim();
    if (!name) return;
    setSaveMessage(null);
    resolveScenarioStore()
      .then((store) => store.save(name, state))
      .then((scenario) => {
        setScenarios((prev) => [...prev, scenario]);
        setScenarioName('');
        setSaveMessage(`Scenario '${scenario.name}' saved`);
      })
      .catch((err: Error) => setSaveMessage(`Save failed: ${err.message}`));
  };

  const loadScenario = (scenario: Scenario) => {
    setState(JSON.parse(JSON.stringify(scenario.state)) as SandboxState);
  };

  const deleteScenario = async (scenario: Scenario) => {
    try {
      const store = await resolveScenarioStore();
      await store.remove(scenario.id);
      setScenarios((prev) => prev.filter((s) => s.id !== scenario.id));
      // Drop any comparison pointing at the deleted scenario
      setCompareA((prev) => (prev === scenario.id ? '' : prev));
      setCompareB((prev) => (prev === scenario.id ? '' : prev));
      setSaveMessage(`Scenario '${scenario.name}' deleted`);
    } catch (err) {
      setSaveMessage(`Delete failed: ${err instanceof Error ? err.message : 'unknown error'}`);
    }
  };

  const scenarioA = scenarios.find((s) => s.id === compareA);
  const scenarioB = scenarios.find((s) => s.id === compareB);
  const compareDiff = useMemo(() => {
    if (!scenarioA || !scenarioB) return null;
    return diffInstanceMaps(aggregateInstances(scenarioA.state), aggregateInstances(scenarioB.state));
  }, [scenarioA, scenarioB]);

  return (
    <>
      <h2 className="mb-1 text-2xl font-bold text-gray-900">Sandbox</h2>
      <p className="mb-6 text-sm text-gray-500">
        Hypothetical racks and droplets for what-if planning. Nothing here touches real
        Outposts.
      </p>

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <label className={FIELD_LABEL_CLASSES}>
          Generation
          <Select
            data-testid="new-rack-generation"
            value={newRackGeneration}
            onChange={(e) => setNewRackGeneration(e.target.value)}
          >
            {Object.keys(INSTANCE_CATALOG).map((generation) => (
              <option key={generation} value={generation}>
                {generation}
              </option>
            ))}
          </Select>
        </label>
        <Button data-testid="add-rack" onClick={addRack}>
          Add rack
        </Button>
        {state.racks.length > 0 && (
          <Button
            variant="secondary"
            data-testid="clear-sandbox"
            onClick={() => setState(EMPTY_SANDBOX)}
          >
            Clear sandbox
          </Button>
        )}
      </div>

      <ImportFromConnected
        onImportAsset={(asset, outpostId) => {
          const result = cloneAssetToSandbox(state, asset, outpostId);
          if (result.ok) setState(result.state);
        }}
        onImportRack={(rackId, assets, outpostId) => {
          const result = cloneRackToSandbox(state, rackId, assets, outpostId);
          if (result.ok) setState(result.state);
        }}
      />

      {state.racks.length === 0 && (
        <p className={STATE_MESSAGE_CLASSES} data-testid="sandbox-empty">
          The sandbox is empty. Add a rack or import a real asset configuration below.
        </p>
      )}

      {state.racks.map((rack) => (
        <div key={rack.id} className="mb-8" data-testid="sandbox-rack">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-bold text-gray-900">{rack.name}</h3>
            <Chip variant="family">{rack.generation}</Chip>
            <AddDroplet rack={rack} onAdd={(family) =>
              updateRack(rack.id, (r) => ({
                ...r,
                droplets: [...r.droplets, { id: newId('droplet'), family, instances: {} }],
              }))
            } />
            {rack.droplets.length > 0 && (
              <Button
                variant="secondary"
                small
                data-testid="export-rack"
                title="Save one InstancePools JSON per droplet in this rack"
                onClick={() => rack.droplets.forEach((droplet) => exportDroplet(droplet, rack.name))}
              >
                Export rack JSONs
              </Button>
            )}
            <Button
              variant="secondary"
              small
              data-testid="remove-rack"
              onClick={() => removeRack(rack.id)}
            >
              Remove rack
            </Button>
          </div>
          {rack.droplets.map((droplet) => (
            <DropletCard
              key={droplet.id}
              rack={rack}
              droplet={droplet}
              onChange={(instances) =>
                updateRack(rack.id, (r) => ({
                  ...r,
                  droplets: r.droplets.map((d) => (d.id === droplet.id ? { ...d, instances } : d)),
                }))
              }
              onRemove={() =>
                updateRack(rack.id, (r) => ({
                  ...r,
                  droplets: r.droplets.filter((d) => d.id !== droplet.id),
                }))
              }
            />
          ))}
        </div>
      ))}

      <section className="mt-10 border-t border-gray-200 pt-5" aria-label="Scenarios">
        <h3 className="mb-2 text-base font-bold text-gray-900">Scenarios</h3>
        <div className="mb-5 flex flex-wrap items-end gap-3">
          <Input
            type="text"
            placeholder="Scenario name"
            data-testid="scenario-name"
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
          />
          <Button
            data-testid="save-scenario"
            disabled={scenarioName.trim().length === 0}
            onClick={saveScenario}
          >
            Save scenario
          </Button>
        </div>
        {saveMessage && (
          <p className={STATE_MESSAGE_CLASSES} data-testid="save-message">
            {saveMessage}
          </p>
        )}

        {scenarios.length > 0 && (
          <>
            <ul className="mb-4 divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
              {scenarios.map((scenario) => (
                <li
                  key={scenario.id}
                  className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm text-gray-900"
                  data-testid={`scenario-${scenario.name}`}
                >
                  <span>
                    <strong>{scenario.name}</strong> ·{' '}
                    {new Date(scenario.createdAt).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      small
                      data-testid={`load-${scenario.name}`}
                      onClick={() => loadScenario(scenario)}
                    >
                      Load
                    </Button>
                    <DeleteScenarioButton
                      name={scenario.name}
                      onDelete={() => deleteScenario(scenario)}
                    />
                  </span>
                </li>
              ))}
            </ul>

            <div className="mb-5 flex flex-wrap items-end gap-3">
              <label className={FIELD_LABEL_CLASSES}>
                Compare A
                <Select
                  data-testid="compare-a"
                  value={compareA}
                  onChange={(e) => setCompareA(e.target.value)}
                >
                  <option value="">—</option>
                  {scenarios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </label>
              <label className={FIELD_LABEL_CLASSES}>
                Compare B
                <Select
                  data-testid="compare-b"
                  value={compareB}
                  onChange={(e) => setCompareB(e.target.value)}
                >
                  <option value="">—</option>
                  {scenarios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </label>
            </div>

            {scenarioA && scenarioB && compareDiff && (
              <div
                className="rounded-lg border border-gray-200 bg-white p-5 shadow"
                data-testid="compare-panel"
              >
                <TotalsLine label={scenarioA.name} state={scenarioA.state} />
                <TotalsLine label={scenarioB.name} state={scenarioB.state} />
                <h4 className="mb-2 mt-4 text-base font-bold text-gray-900">
                  Instance changes (A → B)
                </h4>
                <DiffTable diff={compareDiff} />
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}

const WHOLE_RACK = '__whole_rack__';

/**
 * Pull a real configuration from connected mode into the sandbox: a whole
 * rack (one droplet per asset) or a single asset. This lives only in the
 * sandbox so production views stay purely operational.
 */
function ImportFromConnected({
  onImportAsset,
  onImportRack,
}: {
  onImportAsset: (asset: AssetSummary, outpostId: string) => void;
  onImportRack: (rackId: string, assets: AssetSummary[], outpostId: string) => void;
}) {
  const [outposts, setOutposts] = useState<OutpostSummary[]>([]);
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [outpostId, setOutpostId] = useState('');
  const [rackId, setRackId] = useState('');
  const [scope, setScope] = useState('');

  useEffect(() => {
    outpostRepository
      .getAllOutposts()
      .then(setOutposts)
      .catch(() => {});
  }, []);

  useEffect(() => {
    setAssets([]);
    setRackId('');
    setScope('');
    if (!outpostId) return;
    outpostRepository
      .getAssets(outpostId)
      .then(setAssets)
      .catch(() => {});
  }, [outpostId]);

  const rackIds = useMemo(
    () => [...new Set(assets.map((a) => a.rackId))].sort((a, b) => a.localeCompare(b)),
    [assets],
  );
  const rackAssets = useMemo(
    () => assets.filter((a) => a.rackId === rackId && a.instanceFamilies.length > 0),
    [assets, rackId],
  );

  const importSelection = () => {
    if (scope === WHOLE_RACK) {
      onImportRack(rackId, assets, outpostId);
      return;
    }
    const asset = rackAssets.find((a) => a.assetId === scope);
    if (asset) onImportAsset(asset, outpostId);
  };

  return (
    <div className="mb-5 flex flex-wrap items-end gap-3" data-testid="import-connected">
      <label className={FIELD_LABEL_CLASSES}>
        Import real config: Outpost
        <Select
          data-testid="import-outpost"
          value={outpostId}
          onChange={(e) => setOutpostId(e.target.value)}
        >
          <option value="">—</option>
          {outposts.map((outpost) => (
            <option key={outpost.outpostId} value={outpost.outpostId}>
              {outpost.name}
            </option>
          ))}
        </Select>
      </label>
      <label className={FIELD_LABEL_CLASSES}>
        Rack
        <Select
          data-testid="import-rack"
          value={rackId}
          disabled={rackIds.length === 0}
          onChange={(e) => {
            setRackId(e.target.value);
            setScope('');
          }}
        >
          <option value="">—</option>
          {rackIds.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </Select>
      </label>
      <label className={FIELD_LABEL_CLASSES}>
        Scope
        <Select
          data-testid="import-asset"
          value={scope}
          disabled={!rackId}
          onChange={(e) => setScope(e.target.value)}
        >
          <option value="">—</option>
          {rackAssets.length > 0 && (
            <option value={WHOLE_RACK}>
              Whole rack ({rackAssets.length} asset{rackAssets.length === 1 ? '' : 's'})
            </option>
          )}
          {rackAssets.map((a) => (
            <option key={a.assetId} value={a.assetId}>
              {a.assetId} ({a.instanceFamilies.join(', ')})
            </option>
          ))}
        </Select>
      </label>
      <Button
        variant="secondary"
        data-testid="import-config"
        disabled={!scope}
        onClick={importSelection}
      >
        Import config
      </Button>
    </div>
  );
}

function AddDroplet({ rack, onAdd }: { rack: SandboxRack; onAdd: (family: string) => void }) {
  // Only compute droplet families; networking bare-metal entries are not
  // slottable capacity
  const families = computeFamilyKeys(rack.generation);
  const [family, setFamily] = useState(families[0] ?? '');
  return (
    <span className="ml-auto flex items-center gap-2">
      <Select
        aria-label="Droplet family"
        data-testid="new-droplet-family"
        value={family}
        onChange={(e) => setFamily(e.target.value)}
      >
        {families.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
      </Select>
      <Button
        variant="secondary"
        small
        data-testid="add-droplet"
        onClick={() => family && onAdd(family)}
      >
        Add droplet
      </Button>
    </span>
  );
}
